// Email notification service using Resend API
// All emails sent from noreply@laadlink.nl

const RESEND_API_KEY = process.env.REACT_APP_RESEND_API_KEY
const FROM_EMAIL = 'Buurtlader <noreply@laadlink.nl>'
const APP_URL = process.env.REACT_APP_URL || 'https://laadlink.nl'

const sendEmail = async ({ to, subject, html }) => {
  if (!RESEND_API_KEY) {
    console.warn('Resend API key not configured — email not sent')
    return
  }

  try {
    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${RESEND_API_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        from: FROM_EMAIL,
        to,
        subject,
        html
      })
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Resend error:', error)
    }
  } catch (error) {
    console.error('Email send failed:', error)
  }
}

const baseStyle = `
  font-family: 'DM Sans', Arial, sans-serif;
  max-width: 560px;
  margin: 0 auto;
  background: #f7f4ee;
  padding: 32px 24px;
  border-radius: 16px;
`

const cardStyle = `
  background: white;
  border-radius: 14px;
  padding: 24px;
  margin: 16px 0;
  border: 1px solid #ede9e0;
`

const greenBtn = `
  display: inline-block;
  background: #3d9e5f;
  color: white;
  padding: 14px 28px;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 700;
  font-size: 15px;
  margin-top: 16px;
`

const logo = `
  <div style="display:flex;align-items:center;gap:8px;margin-bottom:24px;">
    <div style="width:10px;height:10px;border-radius:50%;background:#3d9e5f;"></div>
    <span style="font-weight:900;font-size:20px;color:#1a2e20;">Buurtlader</span>
  </div>
`

// ── EMAIL 1: New booking request → owner ────────────────────
export const sendBookingRequestEmail = async ({
  ownerEmail,
  ownerName,
  chargerName,
  driverMessage,
  date,
  startTime,
  endTime,
  durationHours,
  totalPriceEst,
  bookingId
}) => {
  const subject = `Nieuwe boekingsaanvraag voor ${chargerName}`

  const html = `
    <div style="${baseStyle}">
      ${logo}
      <h2 style="color:#1a2e20;font-size:22px;margin:0 0 8px;">
        Je hebt een nieuwe boekingsaanvraag! 🔌
      </h2>
      <p style="color:#7a9a82;font-size:14px;margin:0 0 16px;">
        Hoi ${ownerName || 'eigenaar'}, iemand wil laden bij jouw laadpaal.
      </p>

      <div style="${cardStyle}">
        <div style="font-weight:700;font-size:16px;color:#1a2e20;margin-bottom:12px;">
          ${chargerName}
        </div>
        <table style="width:100%;border-collapse:collapse;">
          <tr>
            <td style="color:#7a9a82;font-size:13px;padding:5px 0;">Datum</td>
            <td style="color:#1a2e20;font-weight:600;font-size:13px;text-align:right;">${date}</td>
          </tr>
          <tr>
            <td style="color:#7a9a82;font-size:13px;padding:5px 0;">Tijd</td>
            <td style="color:#1a2e20;font-weight:600;font-size:13px;text-align:right;">${startTime} – ${endTime}</td>
          </tr>
          <tr>
            <td style="color:#7a9a82;font-size:13px;padding:5px 0;">Duur</td>
            <td style="color:#1a2e20;font-weight:600;font-size:13px;text-align:right;">${durationHours}u</td>
          </tr>
          <tr>
            <td style="color:#7a9a82;font-size:13px;padding:5px 0;">Geschatte opbrengst</td>
            <td style="color:#3d9e5f;font-weight:900;font-size:15px;text-align:right;">€${totalPriceEst}</td>
          </tr>
        </table>

        ${driverMessage ? `
          <div style="margin-top:14px;padding:10px 14px;background:#f7f4ee;border-radius:10px;font-size:13px;color:#3d5a45;font-style:italic;">
            "${driverMessage}"
          </div>
        ` : ''}
      </div>

      <p style="color:#7a9a82;font-size:13px;text-align:center;">
        Open de Buurtlader app om te accepteren of weigeren.
      </p>

      <div style="text-align:center;">
        <a href="${APP_URL}" style="${greenBtn}">
          Open Buurtlader →
        </a>
      </div>

      <p style="color:#c2e8cf;font-size:11px;text-align:center;margin-top:24px;">
        Je ontvangt dit bericht omdat je een laadpaal hebt geregistreerd op Buurtlader.<br/>
        <a href="${APP_URL}/privacy" style="color:#c2e8cf;">Privacybeleid</a>
      </p>
    </div>
  `

  await sendEmail({ to: ownerEmail, subject, html })
}

// ── EMAIL 2: Booking accepted → driver ─────────────────────
export const sendBookingAcceptedEmail = async ({
  driverEmail,
  driverName,
  chargerName,
  chargerAddress,
  chargerCity,
  date,
  startTime,
  endTime,
  totalPriceEst,
  ownerResponse
}) => {
  const subject = `✅ Boeking geaccepteerd — ${chargerName}`

  const html = `
    <div style="${baseStyle}">
      ${logo}
      <h2 style="color:#1a2e20;font-size:22px;margin:0 0 8px;">
        Je boeking is geaccepteerd! ✅
      </h2>
      <p style="color:#7a9a82;font-size:14px;margin:0 0 16px;">
        Hoi ${driverName || 'rijder'}, goed nieuws — je kunt laden!
      </p>

      <div style="${cardStyle}">
        <div style="font-weight:700;font-size:16px;color:#1a2e20;margin-bottom:4px;">
          ${chargerName}
        </div>
        <div style="color:#7a9a82;font-size:13px;margin-bottom:14px;">
          📍 ${chargerAddress}, ${chargerCity}
        </div>
        <table style="width:100%;border-collapse:collapse;">
          <tr>
            <td style="color:#7a9a82;font-size:13px;padding:5px 0;">Datum</td>
            <td style="color:#1a2e20;font-weight:600;font-size:13px;text-align:right;">${date}</td>
          </tr>
          <tr>
            <td style="color:#7a9a82;font-size:13px;padding:5px 0;">Tijd</td>
            <td style="color:#1a2e20;font-weight:600;font-size:13px;text-align:right;">${startTime} – ${endTime}</td>
          </tr>
          <tr>
            <td style="color:#7a9a82;font-size:13px;padding:5px 0;">Geschatte kosten</td>
            <td style="color:#3d9e5f;font-weight:900;font-size:15px;text-align:right;">€${totalPriceEst}</td>
          </tr>
        </table>

        ${ownerResponse ? `
          <div style="margin-top:14px;padding:10px 14px;background:#e8f5ee;border:1px solid #c2e8cf;border-radius:10px;font-size:13px;color:#3d5a45;">
            <strong style="color:#3d9e5f;">Bericht van eigenaar:</strong><br/>
            ${ownerResponse}
          </div>
        ` : ''}
      </div>

      <div style="text-align:center;">
        <a href="${APP_URL}" style="${greenBtn}">
          Open Buurtlader →
        </a>
      </div>

      <p style="color:#c2e8cf;font-size:11px;text-align:center;margin-top:24px;">
        <a href="${APP_URL}/privacy" style="color:#c2e8cf;">Privacybeleid</a>
      </p>
    </div>
  `

  await sendEmail({ to: driverEmail, subject, html })
}

// ── EMAIL 3: Booking rejected → driver ─────────────────────
export const sendBookingRejectedEmail = async ({
  driverEmail,
  driverName,
  chargerName,
  date,
  startTime,
  endTime,
  ownerResponse
}) => {
  const subject = `Boeking niet beschikbaar — ${chargerName}`

  const html = `
    <div style="${baseStyle}">
      ${logo}
      <h2 style="color:#1a2e20;font-size:22px;margin:0 0 8px;">
        Boeking niet geaccepteerd
      </h2>
      <p style="color:#7a9a82;font-size:14px;margin:0 0 16px;">
        Hoi ${driverName || 'rijder'}, helaas is je boeking niet geaccepteerd. Geen zorgen — er zijn meer laadpalen beschikbaar.
      </p>

      <div style="${cardStyle}">
        <div style="font-weight:700;font-size:16px;color:#1a2e20;margin-bottom:14px;">
          ${chargerName}
        </div>
        <table style="width:100%;border-collapse:collapse;">
          <tr>
            <td style="color:#7a9a82;font-size:13px;padding:5px 0;">Datum</td>
            <td style="color:#1a2e20;font-weight:600;font-size:13px;text-align:right;">${date}</td>
          </tr>
          <tr>
            <td style="color:#7a9a82;font-size:13px;padding:5px 0;">Tijd</td>
            <td style="color:#1a2e20;font-weight:600;font-size:13px;text-align:right;">${startTime} – ${endTime}</td>
          </tr>
        </table>

        ${ownerResponse ? `
          <div style="margin-top:14px;padding:10px 14px;background:#fef2f2;border:1px solid #fecaca;border-radius:10px;font-size:13px;color:#7f1d1d;">
            <strong>Reden:</strong> ${ownerResponse}
          </div>
        ` : ''}
      </div>

      <p style="color:#7a9a82;font-size:13px;text-align:center;">
        Zoek een andere laadpaal in de buurt.
      </p>

      <div style="text-align:center;">
        <a href="${APP_URL}" style="${greenBtn}">
          Andere laadpaal zoeken →
        </a>
      </div>

      <p style="color:#c2e8cf;font-size:11px;text-align:center;margin-top:24px;">
        <a href="${APP_URL}/privacy" style="color:#c2e8cf;">Privacybeleid</a>
      </p>
    </div>
  `

  await sendEmail({ to: driverEmail, subject, html })
}

// ── EMAIL 4: Booking cancelled → owner ─────────────────────
export const sendBookingCancelledEmail = async ({
  ownerEmail,
  ownerName,
  chargerName,
  date,
  startTime,
  endTime
}) => {
  const subject = `Boeking geannuleerd — ${chargerName}`

  const html = `
    <div style="${baseStyle}">
      ${logo}
      <h2 style="color:#1a2e20;font-size:22px;margin:0 0 8px;">
        Een boeking is geannuleerd
      </h2>
      <p style="color:#7a9a82;font-size:14px;margin:0 0 16px;">
        Hoi ${ownerName || 'eigenaar'}, een rijder heeft zijn boeking geannuleerd. Het tijdslot is weer vrij.
      </p>

      <div style="${cardStyle}">
        <div style="font-weight:700;font-size:16px;color:#1a2e20;margin-bottom:14px;">
          ${chargerName}
        </div>
        <table style="width:100%;border-collapse:collapse;">
          <tr>
            <td style="color:#7a9a82;font-size:13px;padding:5px 0;">Datum</td>
            <td style="color:#1a2e20;font-weight:600;font-size:13px;text-align:right;">${date}</td>
          </tr>
          <tr>
            <td style="color:#7a9a82;font-size:13px;padding:5px 0;">Tijd</td>
            <td style="color:#1a2e20;font-weight:600;font-size:13px;text-align:right;">${startTime} – ${endTime}</td>
          </tr>
        </table>
      </div>

      <p style="color:#c2e8cf;font-size:11px;text-align:center;margin-top:24px;">
        <a href="${APP_URL}/privacy" style="color:#c2e8cf;">Privacybeleid</a>
      </p>
    </div>
  `

  await sendEmail({ to: ownerEmail, subject, html })
}
